import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { backtestAPI } from '../../api/backtests';
import { rowHeadline } from '../backtests/results/headline';
import { fmtPct, fmtNum } from '../backtests/results/ui';
import HypotheticalLegend from '../backtests/results/HypotheticalLegend';

// Under the strategy builder (DWLF-305; canvas board BuildStrategy): this strategy's latest completed
// backtest, with the API's headline numbers, and a way to run another.
const tok = (t) => `var(--dwlf-${t})`;
const LABEL = { fontFamily: '"Space Mono", monospace', textTransform: 'uppercase', letterSpacing: '0.12em' };
const MONO = '"IBM Plex Mono", monospace';
const linkSx = { ...LABEL, fontSize: '0.625rem', textDecoration: 'none', height: 36, px: 1.5, display: 'inline-flex', alignItems: 'center' };

// /backtests has no strategy filter, so walk its cursor pages for this strategy's runs. Bounded: past
// MAX_PAGES the strip says it found none recently rather than claiming the strategy was never tested.
const PAGE = 100;
const MAX_PAGES = 10;
export async function findLatestRun(strategyId, api = backtestAPI) {
  let cursor;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const res = await api.getBacktests({ status: 'completed', limit: PAGE, cursor });
    const body = res.data?.data || res.data || {}; // same shape backtestsSlice reads
    const latest = (body.items || [])
      .filter((b) => b.strategyId === strategyId)
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
    if (latest) return { latest, exhausted: false };
    cursor = body.cursor;
    if (!cursor) return { latest: null, exhausted: true };
  }
  return { latest: null, exhausted: false };
}

export default function StrategyBacktestStrip({ strategyId }) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { latest, exhausted } = await findLatestRun(strategyId);
        if (!latest) { if (live) setState({ loading: false, none: exhausted, notRecent: !exhausted }); return; }
        const res = await backtestAPI.getBacktestSummaryResults(latest.requestId);
        if (live) setState({ loading: false, run: latest, h: rowHeadline(res.data?.data?.results || res.data?.results) });
      } catch (e) {
        if (live) setState({ loading: false, failed: true });
      }
    })();
    return () => { live = false; };
  }, [strategyId]);

  const { run, h } = state;
  const stats = h ? [
    ['Return', fmtPct(h.returnPct, 1), h.returnPct == null ? 'ink-55' : h.returnPct >= 0 ? 'up' : 'down'],
    ['Max DD', h.maxDrawdownPct == null ? '—' : `−${Math.abs(h.maxDrawdownPct).toFixed(1)}%`, 'down'],
    ['Win', h.winRatePct == null ? '—' : `${h.winRatePct.toFixed(0)}%`, 'text'],
    ['PF', fmtNum(h.profitFactor), 'text'],
    ['Trades', h.trades == null ? '—' : String(h.trades), 'text'],
  ] : [];

  return (
    <Box component="section" aria-label="Latest backtest of this strategy"
      sx={{ flexShrink: 0, borderTop: `1px solid ${tok('ink-08')}`, backgroundColor: tok('sunk'), px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
      <Box sx={{ minWidth: 220 }}>
        <Typography sx={{ ...LABEL, fontSize: '0.625rem', color: tok('ink-50') }}>Latest backtest</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.8125rem', color: tok(state.failed ? 'down' : 'text'), mt: 0.5 }}>
          {state.loading && 'Loading…'}
          {state.failed && "Couldn't load backtests."}
          {state.none && 'Not backtested yet.'}
          {state.notRecent && `No backtest of this strategy in your latest ${PAGE * MAX_PAGES} runs.`}
          {run && `${(run.symbols || []).slice(0, 3).join(' · ')}${(run.symbols || []).length > 3 ? ' …' : ''} · ${String(run.startDate || '').slice(0, 4)}–${String(run.endDate || '').slice(0, 4)}`}
        </Typography>
      </Box>
      {stats.map(([k, v, tone]) => (
        <Box key={k}>
          <Typography sx={{ ...LABEL, fontSize: '0.5625rem', color: tok('ink-50') }}>{k}</Typography>
          <Typography sx={{ fontFamily: MONO, fontSize: '0.9375rem', color: tok(tone) }}>{v}</Typography>
        </Box>
      ))}
      <Box sx={{ flex: 1 }} />
      {run && <Typography component={RouterLink} to={`/backtests/${run.requestId}/results`} sx={{ ...linkSx, color: tok('accent') }}>Open results →</Typography>}
      <Typography component={RouterLink} to={`/backtests/new?strategyId=${encodeURIComponent(strategyId)}`}
        sx={{ ...linkSx, color: tok('text'), border: `1px solid ${tok('ink-20')}`, '&:hover': { borderColor: tok('accent'), color: tok('accent') } }}>
        Run backtest
      </Typography>
      {stats.length > 0 && <HypotheticalLegend sx={{ flexBasis: '100%' }} />}
    </Box>
  );
}
